import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SearchIcon from '@mui/icons-material/Search';
import { IconButton, InputAdornment, InputBase, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';
import { useDebounce } from 'react-use';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { useTranslation } from '../../../../../services/translation/hooks/useTranslation';
import TooltipPopup from '../../../../commonUI/tooltips/TooltipPopup';
import { selectSearchValue, setSearchValue } from '../../../scansSlice';
import { TooltipScanSearch } from './TooltipScanSearch';
const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  width: '100%',
}));

export const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  padding: '0.5rem',
  '& .MuiInputBase-input': {
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
  },
}));

const ScansSearchBar: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const initialSearchVal = useAppSelector(selectSearchValue);
  const [val, setVal] = useState(initialSearchVal);

  const handleChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setVal(event.target.value);
  };

  useDebounce(
    () => {
      dispatch(
        setSearchValue({
          newValue: val,
        })
      );
    },
    400,
    [val]
  );

  const handleClickClearSearch = () => setVal('');

  const handleMouseDownClearSearch = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  return (
    <TooltipPopup
      colorScheme='extraDark'
      placement='bottom'
      popupComponent={<TooltipScanSearch />}
      customBgColor={theme.vars.palette.background.paper}
      border
      arrow={false}
    >
      <Search sx={{ width: '100%' }}>
        <StyledInputBase
          placeholder={`${t('Search')}...`}
          inputProps={{ 'aria-label': 'search' }}
          value={val}
          onChange={handleChange}
          fullWidth
          sx={{
            width: '100%',
            color: theme.palette.text.primary,
            background: theme.vars.palette.background.paper,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: theme.vars.palette.divider,
            borderRadius: '0.5rem',
          }}
          startAdornment={
            <InputAdornment position='start'>
              <SearchIconWrapper>
                <SearchIcon
                  sx={(theme) => ({
                    fill: theme.palette.text.primary,
                  })}
                />
              </SearchIconWrapper>
            </InputAdornment>
          }
          endAdornment={
            <InputAdornment position='end'>
              <IconButton
                aria-label='Clear Search'
                onClick={handleClickClearSearch}
                onMouseDown={handleMouseDownClearSearch}
                edge='end'
                sx={{ mr: '0.2rem' }}
              >
                <HighlightOffIcon
                  sx={(theme) => ({ color: theme.vars.palette.grey[700] })}
                />
              </IconButton>
            </InputAdornment>
          }
        />
      </Search>
    </TooltipPopup>
  );
};

export default ScansSearchBar;
